import React, { useState, useEffect } from "react";
import { useIntl } from "gatsby-plugin-react-intl";

import Layout from "../../components/Layout";
import HeroV2, { HeroProps } from "../../components/HeroV2";
import Tab from "../../components/Tab";
import GridSection from "../../components/GridSection";
import { getDagInfos } from "../../services/CoinGeckoService";
import ImageTextV2 from "../../components/ImageTextV2";
import { ImageTextV2Props } from "../../components/ImageTextV2/types";
import TabCardGroup from "../../components/TabCardGroup";
import TabCardV2, { TabCardV2Props } from "../../components/TabCardV2";

import ExternalLinks from "../../utils/externalLinks";
import InternalLinks from "../../utils/internalLinks";

import {
  formatInBillion,
  formatInMillion,
  formatInThousands,
} from "../../utils/numberFormatter";

//images
import stargazerC14 from "../../img/get-dag/buy_stargazer_iphones.png";
import buyCoinbase from "../../img/get-dag/buy_coinbase_iphones.png";

//logos
import kucoin from "../../img/get-dag/kucoin-logo-icon.png";
import exolix from "../../img/get-dag/exolix-logo-icon.png";
import gateio from "../../img/get-dag/gateio-logo-icon.png";
import uphold from "../../img/get-dag/uphold-logo-icon.png";
import c14 from "../../img/get-dag/c14-logo-icon.png";
import bridge3adao from "../../img/get-dag/3adao-bridge-logo-icon.png";
import stargazer from "../../img/get-dag/stargazer-logo-icon.png";
import simplex from "../../img/get-dag/simplex-logo-icon.png";
import topper from "../../img/get-dag/topper-logo-icon.png";
import pacaSwap from "../../img/get-dag/pacaswap-logo-icon.png";
import mexc from "../../img/get-dag/mexc-logo-icon.png";
import weex from "../../img/get-dag/weex-logo-icon.png";
import lbank from "../../img/get-dag/lbank-logo-icon.png";
import ourbit from "../../img/get-dag/ourbit-logo-icon.png";
import xt from "../../img/get-dag/xt-logo-icon.png";


import Dtm from "../../img/icons/dtm-filled.svg";
import Lattice from "../../img/icons/lattice-filled.svg";
import Graph from "../../img/icons/graph-filled.svg";
import CircleTree from "../../img/icons/circle-tree-filled.svg";
import ServerLogo from "../../img/icons/server.svg";

import {
  GetDagContainer,
  FirstSection,
  SecondSection,
  ThirdSection,
  FifthSection,
  DescriptionContainer,
  StargazerSection,
} from "../../components/styles/get_dag_styles";
import BuySection from "../../components/BuySection";

export const GetDagPage = () => {
  const intl = useIntl();
  const [exchangeDag, setExchangeDag] = useState(true);
  const [buyDag, setBuyDag] = useState(false);
  const [earnDag, setEarnDag] = useState(false);

  const [cards, setCards] = useState<HeroProps["content"]>([]);
  const [exchangeDagCards, setExchangeDagCards] = useState<TabCardV2Props[]>([]);
  const [buyDagCards, setBuyDagCards] = useState<TabCardV2Props[]>([]);
  const [earnCards, setEarnCards] = useState<TabCardV2Props[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const {
        currentPriceUsd,
        marketCapUsd,
        circulatingSupply,
        totalVolumeUsd,
      } = await getDagInfos();

      setCards([
        {
          title: intl.formatMessage({ id: "get_dag_page.dag_price" }),
          value: `$${currentPriceUsd.toFixed(3)}`,
          useInterrogation: true,
          tooltipMessage: intl.formatMessage({ id: "price_tooltip" }),
        },
        {
          title: intl.formatMessage({ id: "get_dag_page.market_cap" }),
          value: `$${formatInMillion(marketCapUsd, 1)}M`,
          useInterrogation: true,
          tooltipMessage: intl.formatMessage({ id: "market_cap_tooltip" }),
        },
        {
          title: intl.formatMessage({ id: "get_dag_page.circulating_supply" }),
          value: `${formatInBillion(circulatingSupply, 2)}B`,
          useInterrogation: true,
          tooltipMessage: intl.formatMessage({
            id: "circulating_supply_tooltip",
          }),
        },
        {
          title: intl.formatMessage({ id: "get_dag_page.volume" }),
          value: `$${formatInThousands(totalVolumeUsd, 0)}K`,
          useInterrogation: true,
          tooltipMessage: intl.formatMessage({ id: "total_amount_tooltip" }),
        },
      ]);

      setExchangeDagCards([
        {
          title: 'Paca Swap',
          description: intl.formatMessage({
            id: "get_dag_page.pacaswap_description",
          }),
          imageSrc: pacaSwap,
          link: InternalLinks.PACASWAP,
          isExternalLink: false
        },
        {
          title: "Kucoin",
          description: intl.formatMessage({
            id: "get_dag_page.kucoin_description",
          }),
          imageSrc: kucoin,
          link: ExternalLinks.KUCOIN,
          isExternalLink: true,
        },
        {
          title: "Uphold",
          description: intl.formatMessage({
            id: "get_dag_page.uphold_description",
          }),
          imageSrc: uphold,
          link: ExternalLinks.UPHOLD,
          isExternalLink: true,
        },
        {
          title: "Exolix",
          description: intl.formatMessage({
            id: "get_dag_page.exolix_description",
          }),
          imageSrc: exolix,
          link: ExternalLinks.EXOLIX,
          isExternalLink: true,
        },
        {
          title: "Gate.io",
          description: intl.formatMessage({
            id: "get_dag_page.gateio_description",
          }),
          imageSrc: gateio,
          link: ExternalLinks.GATEIO,
          isExternalLink: true,
        },
        {
          title: "MEXC",
          description: intl.formatMessage({
            id: "get_dag_page.mexc_description",
          }),
          imageSrc: mexc,
          link: ExternalLinks.MEXC,
          isExternalLink: true,
        },
        {
          title: "Weex",
          description: intl.formatMessage({
            id: "get_dag_page.weex_description",
          }),
          imageSrc: weex,
          link: ExternalLinks.WEEX,
          isExternalLink: true,
        },
        {
          title: "LBank",
          description: intl.formatMessage({
            id: "get_dag_page.lbank_description",
          }),
          imageSrc: lbank,
          link: ExternalLinks.LBANK,
          isExternalLink: true,
        },
        {
          title: "Ourbit",
          description: intl.formatMessage({
            id: "get_dag_page.ourbit_description",
          }),
          imageSrc: ourbit,
          link: ExternalLinks.OURBIT,
          isExternalLink: true,
        },
        {
          title: "XT Com",
          description: intl.formatMessage({
            id: "get_dag_page.xt_description",
          }),
          imageSrc: xt,
          link: ExternalLinks.XT,
          isExternalLink: true,
        }
      ]);

      setBuyDagCards([
        {
          title: "Stargazer Wallet",
          description: intl.formatMessage({
            id: "get_dag_page.stargazer_wallet_description",
          }),
          link: InternalLinks.STARGAZER_WALLET,
          imageSrc: stargazer,
          isExternalLink: false,
        },
        {
          title: "Simplex",
          description: intl.formatMessage({
            id: "get_dag_page.simplex_description",
          }),
          link: ExternalLinks.LATTICE_SIMPLEX,
          imageSrc: simplex,
          isExternalLink: true,
        },
        {
          title: "Topper",
          description: intl.formatMessage({
            id: "get_dag_page.topper_description",
          }),
          link: ExternalLinks.LATTICE_SIMPLEX,
          imageSrc: topper,
          isExternalLink: true,
        },
        {
          title: "C14",
          description: "C14 allows customers to buy a wide range of crypto tokens with the lowest fees on the market via traditional payment methods.",
          link: ExternalLinks.C14,
          imageSrc: c14,
          isExternalLink: true,
        },
        {
          title: "3A DAO Ethereum Bridge",
          description: "A Constellation Network - Ethereum bridge for DAG and wDAG built by 3A DAO.",
          link: ExternalLinks.BRIDGE_3A_DAO,
          imageSrc: bridge3adao,
          isExternalLink: true,
        },
      ]);

      setEarnCards([
        {
          title: "Stargazer Wallet",
          description: intl.formatMessage({
            id: "get_dag_page.dtm_description",
          }),
          link: InternalLinks.DTM,
          iconComponent: Dtm,
          isExternalLink: false,
        },
        {
          title: "Lattice Gateway",
          description: intl.formatMessage({
            id: "get_dag_page.lattice_gateway_description",
          }),
          link: ExternalLinks.LATTICE_GATEWAY,
          iconComponent: Lattice,
          isExternalLink: true,
        },
        {
          title: "Run a node",
          description: intl.formatMessage({
            id: "get_dag_page.run_a_node_description",
          }),
          link: ExternalLinks.RUN_A_NODE,
          iconComponent: Graph,
          isExternalLink: true,
        },
        {
          title: "Delegated Staking",
          description: intl.formatMessage({
            id: "get_dag_page.delegated_staking_description",
          }),
          link: InternalLinks.DELEGATED_STAKING,
          iconComponent: ServerLogo,
          isExternalLink: false,
        },
        {
          title: "Join our affiliate program",
          description: intl.formatMessage({
            id: "get_dag_page.join_our_description",
          }),
          link: ExternalLinks.AFFILIATE_PROGRAM,
          iconComponent: CircleTree,
          isExternalLink: true,
        },
      ]);
    };

    fetchData();
  }, []);

  const tabTitles = [
    {
      header: intl.formatMessage({ id: "get_dag_page.exchange_dag" }),
      onPress: () => {
        setExchangeDag(true);
        setBuyDag(false);
        setEarnDag(false);
      },
      pressed: exchangeDag,
    },
    {
      header: intl.formatMessage({ id: "get_dag_page.buy_dag" }),
      onPress: () => {
        setExchangeDag(false);
        setBuyDag(true);
        setEarnDag(false);
      },
      pressed: buyDag,
    },
    {
      header: intl.formatMessage({ id: "get_dag_page.earn_dag" }),
      onPress: () => {
        setExchangeDag(false);
        setBuyDag(false);
        setEarnDag(true);
      },
      pressed: earnDag,
    },
  ];

  const imageTextV2Props: Record<string, Partial<ImageTextV2Props>> = {
    "hero": {
      textProps: {
        customTextWidth: "50%",
        customTitleFontSize: "64px",
        customTitleLineHeight: "1.1",
        customTitleLetterSpacing: "-1.4px",
        customTitleFontSizeMobile: "40px",
        title: intl.formatMessage( { id: "get_dag_page.hero_title_p1"}),
        highlightWord: intl.formatMessage( { id: "get_dag_page.hero_title_p2" }),
        highlightWordBreak: true,
        highlightWordColor: "#9EBCFA",
        descriptions: [ {
          text: intl.formatMessage( { id: "get_dag_page.hero_description" }),
          customFontFamily: "Inter",
        } ]
      },
    buttonGroupProps: {
        buttons: [
          {
            label: intl.formatMessage( { id: "get_dag_page.ios_button" }),
            link: ExternalLinks.COINBASE_IOS,
            isExternal: true,
            type: 'secondary'
          },
          {
            label: intl.formatMessage( { id: "get_dag_page.android_button" }),
            link: ExternalLinks.COINBASE_ANDROID,
            isExternal: true,
            type: 'secondary'
          },
          {
            label: intl.formatMessage( { id: "get_dag_page.chrome_button" }),
            link: ExternalLinks.COINBASE_CHROME,
            isExternal: true,
            type: 'secondary'
          },
        ]
      },
      imageProps: {
        imagePosition: "right",
        imagePositionMobile: {
          position: "top",
          breakpoint: "sm"
        },
        imageSrc: buyCoinbase,
        imageAlt: "Buy DAG on Coinbase",
        imageCustomWidths: {
          lg: {
            width: "398.475px",
            aspectRatio: "398.475/527",
          },
          md: {
            width: '306px',
            aspectRatio: '306/406'
          },
          sm: {
            width: '319px',
            aspectRatio: '319/424'
          }
        }
      }
    },
    "buy_dag_on_stargazer_section": {
      textProps: {
        customTextWidth: "50%",
        customTitleFontSize: "64px",
        customTitleLetterSpacing: "-1.4px",
        customTitleLineHeight: "1.1",
        customTitleFontSizeMobile: "40px",
        title: intl.formatMessage( { id: "get_dag_page.buy_dag_on_stargazer_title_p1"}),
        highlightWord: intl.formatMessage( { id: "get_dag_page.buy_dag_on_stargazer_title_p2" }),
        highlightWordBreak: true,
        highlightWordColor: "#9369F7",
        descriptions: [ {
          text: intl.formatMessage( { id: "get_dag_page.buy_dag_on_stargazer_description" }),
          customFontFamily: "Inter",
        } ]
      },
      buttonGroupProps: {
        buttons: [
          {
            label: intl.formatMessage( { id: "get_dag_page.ios_button" }),
            link: ExternalLinks.STARGAZER_WALLET_IOS,
            isExternal: true,
            type: 'secondary'
          },
          {
            label: intl.formatMessage( { id: "get_dag_page.android_button" }),
            link: ExternalLinks.STARGAZER_WALLET_ANDROID,
            isExternal: true,
            type: 'secondary'
          },
          {
            label: intl.formatMessage( { id: "get_dag_page.chrome_button" }),
            link: ExternalLinks.STARGAZER_WALLET_CHROME,
            isExternal: true,
            type: 'secondary'
          },
        ]
      },
      // sectionType: "hero",
      imageProps: {
        imagePositionMobile: {
          position: "top",
          breakpoint: "sm"
        },
        imagePosition: "left",
        imageSrc: stargazerC14,
        imageAlt: "Stargazer C14",
        imageCustomWidths: {
          lg: {
            width: "433.952px",
            aspectRatio: "433.952/569",
          },
          md: {
            width: '338px',
            aspectRatio: '338/443'
          },
          sm: {
            width: '350px',
            aspectRatio: '350/458'
          }
        }
      }
    }
  };

  return (
    <Layout noPadding theme="dark" title="Get DAG">
      <GetDagContainer fluid>
        <GridSection>
          <FirstSection>
            <ImageTextV2 {...imageTextV2Props.hero} />
          </FirstSection>
        </GridSection>
        <GridSection>
          <SecondSection>
            <HeroV2 content={cards} />
          </SecondSection>
        </GridSection>
        <GridSection>
          <StargazerSection>
            <ImageTextV2 {...imageTextV2Props.buy_dag_on_stargazer_section} />
          </StargazerSection>
        </GridSection>
        <GridSection>
          <ThirdSection id="buyDag">
            <BuySection />
          </ThirdSection>
        </GridSection>
        <GridSection>
          <FifthSection>
            <DescriptionContainer>
              <h1>{intl.formatMessage({ id: "get_dag_page.i_want_to" })}</h1>
            </DescriptionContainer>
            <Tab titles={tabTitles} />
              {exchangeDag && (
                <TabCardGroup noPadding gridColumnsOnMobile={2}>
                  {exchangeDagCards.map((card) => (
                  <TabCardV2
                    height="180px"
                    key={card.title}
                    imageSrc={card.imageSrc}
                    title={card.title}
                    description={card.description}
                    link={card.link}
                    isExternalLink={card.isExternalLink}
                    hideTextOnSmallMobile={ true }
                    layoutOnMobile="column"
                  />
                ))}
              </TabCardGroup>
            )}
            {buyDag && (
              <TabCardGroup noPadding gridColumnsOnMobile={2}>
                {buyDagCards.map((card) => (
                  <TabCardV2
                    height="180px"
                    key={card.title}
                    imageSrc={card.imageSrc}
                    title={card.title}
                    description={card.description}
                    link={card.link}
                    isExternalLink={card.isExternalLink}
                    hideTextOnSmallMobile={ true }
                    layoutOnMobile="column"
                  />
                ))}
              </TabCardGroup>
            )}
            {earnDag && (
              <TabCardGroup noPadding gridColumnsOnMobile={2}>
                {earnCards.map((card) => (
                  <TabCardV2
                    height="180px"
                    key={card.title}
                    imageSrc={card.imageSrc}
                    title={card.title}
                    description={card.description}
                    link={card.link}
                    iconComponent={card.iconComponent}
                    isExternalLink={card.isExternalLink}
                    hideTextOnSmallMobile={ true }
                    layoutOnMobile="column"
                  />
                ))}
              </TabCardGroup>
            )}
          </FifthSection>
        </GridSection>
      </GetDagContainer>
    </Layout>
  );
};

export default GetDagPage;
